import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./KebabMenu.module.css";

export type KebabSubmenuItem = {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  disabledReason?: string;
};

export type KebabMenuItem = {
  label: string;
  destructive?: boolean;
  // Green: the action that brings something back (Unlock, Resume).
  positive?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  // Leaf item: runs on click. Ignored when `submenu` is set.
  onClick?: () => void;
  // Parent item: renders with a chevron and opens a flyout (to the left,
  // since the kebab sits at the table's right edge) on hover or click.
  submenu?: ReadonlyArray<KebabSubmenuItem>;
};

type Props = {
  items: ReadonlyArray<KebabMenuItem>;
  ariaLabel?: string;
  // "kebab" (default) renders the vertical-dots trigger with the menu
  // right-aligned to it; "chevron" renders a small ▾ with the menu
  // left-aligned — for inline dropdowns next to left-side cell content.
  trigger?: "kebab" | "chevron";
};

type MenuPosition = {
  top: number;
  left?: number;
  right?: number;
};

const MENU_OFFSET_PX = 4;

export const KebabMenu = ({ items, ariaLabel = "Open menu", trigger = "kebab" }: Props): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [pos, setPos] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeAll = useCallback((): void => {
    setOpen(false);
    setOpenSubmenu(null);
  }, []);

  // Compute fixed-viewport coordinates from the trigger's bounding rect.
  // Using `position: fixed` + a portal puts the menu outside any
  // ancestor with `overflow: hidden` / `overflow-x: auto` (which would
  // otherwise clip it or trigger a stray vertical scrollbar on the
  // table container). When the menu would overflow the bottom edge
  // (rows near the end of a long table), it flips above the trigger.
  const recompute = useCallback((): void => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;
    const r = triggerEl.getBoundingClientRect();
    let top = r.bottom + MENU_OFFSET_PX;
    const menuHeight = menuRef.current?.getBoundingClientRect().height ?? 0;
    if (menuHeight > 0 && top + menuHeight > window.innerHeight) {
      top = Math.max(MENU_OFFSET_PX, r.top - MENU_OFFSET_PX - menuHeight);
    }
    if (trigger === "chevron") {
      const left = r.left;
      setPos((prev) => (prev?.top === top && prev.left === left ? prev : { top, left }));
      return;
    }
    const right = window.innerWidth - r.right;
    setPos((prev) => (prev?.top === top && prev.right === right ? prev : { top, right }));
  }, [trigger]);

  useLayoutEffect(() => {
    if (!open) return;
    recompute();
  }, [open, recompute]);

  // Second pass once the menu has rendered and its height is measurable —
  // the first recompute runs before the portal mounts, so a menu that
  // needs to flip upward only gets correct coordinates here. Settles
  // because recompute bails out of setPos once coordinates stabilize.
  useLayoutEffect(() => {
    if (!open || pos === null) return;
    recompute();
  }, [open, pos, recompute]);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (evt: MouseEvent): void => {
      const target = evt.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      closeAll();
    };
    const handleKey = (evt: KeyboardEvent): void => {
      if (evt.key === "Escape") closeAll();
    };
    const handleScrollOrResize = (): void => {
      recompute();
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open, recompute]);

  const menu = open && pos !== null ? (
    <div
      ref={menuRef}
      className={styles.menu}
      role="menu"
      style={{
        top: `${pos.top}px`,
        ...(pos.left !== undefined ? { left: `${pos.left}px` } : {}),
        ...(pos.right !== undefined ? { right: `${pos.right}px` } : {}),
      }}
    >
      {items.map((item) => (
        <div
          key={item.label}
          className={styles.itemWrap}
          onMouseEnter={() => setOpenSubmenu(item.submenu ? item.label : null)}
        >
          <button
            type="button"
            role="menuitem"
            aria-haspopup={item.submenu ? "menu" : undefined}
            aria-expanded={item.submenu ? openSubmenu === item.label : undefined}
            className={`${styles.item} ${item.destructive ? styles.itemDestructive : item.positive ? styles.itemPositive : ""}`}
            disabled={item.disabled}
            title={item.disabled ? item.disabledReason : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (item.disabled) return;
              if (item.submenu) {
                // Open-only (hover usually got here first); closing is
                // hover-away or Escape, so click never double-toggles.
                setOpenSubmenu(item.label);
                return;
              }
              closeAll();
              item.onClick?.();
            }}
          >
            {item.label}
            {item.submenu && <span className={styles.submenuChevron} aria-hidden="true">‹</span>}
          </button>
          {item.submenu && openSubmenu === item.label && (
            <div className={styles.submenu} role="menu">
              {item.submenu.map((sub) => (
                <button
                  key={sub.label}
                  type="button"
                  role="menuitem"
                  className={`${styles.item} ${sub.destructive ? styles.itemDestructive : ""}`}
                  disabled={sub.disabled}
                  title={sub.disabled ? sub.disabledReason : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (sub.disabled) return;
                    closeAll();
                    sub.onClick();
                  }}
                >
                  {sub.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <span className={styles.root} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        className={trigger === "chevron" ? styles.triggerChevron : styles.trigger}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpenSubmenu(null);
          setOpen((v) => !v);
        }}
      >
        {trigger === "chevron" ? (
          <svg viewBox="0 0 24 24" className={styles.chevronIcon} aria-hidden>
            <path d="M6 9.5 12 15.5 18 9.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className={styles.triggerIcon} aria-hidden>
            <circle cx="12" cy="5" r="1.6" fill="currentColor" />
            <circle cx="12" cy="12" r="1.6" fill="currentColor" />
            <circle cx="12" cy="19" r="1.6" fill="currentColor" />
          </svg>
        )}
      </button>
      {menu !== null ? createPortal(menu, document.body) : null}
    </span>
  );
};
